import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';
// native components
import ClientServicesPhone4644, { clientServicesPhoneNumberLinkUrl } from '../../../../components/clientServicesPhone4644/clientServicesPhone4644';
// styles
import {
  SpanStyledPrintOnly,
  TableStyled,
  TDStyled,
  THeadStyled,
} from './styles';

export default () => (
  <SectionContainer
    id='california-agents-policy-notice'
    backgroundVariant='lightA'
    themeType='lightTheme'>
    <Row align='center'>
      <Col medium={8}>
        <Typography
          component='h1'
          nextElement='typography'>
          Northwestern Mutual California Sales Force <WidowBlock>Privacy Policy</WidowBlock>
        </Typography>
        <Typography
          nextElement='link'
          italic>
          <strong>Last Updated:&#160;</strong> February 9, 2023
        </Typography>
        <Typography nextElement='graphic'>
          <Link
            id='california-agents-policy-notice-printable-link'
            variant='primaryDeemphasize'
            linkUrl='/assets/pdf/legal/northwestern-mutual-california-agents-privacy-notice.pdf'
            isInternalPdf
            ariaLabel='Printable Version: Northwestern Mutual California Sales Force Privacy Policy'>
            Printable Version
          </Link>
        </Typography>
        <Typography
          component='h2'
          nextElement='typography'>
          When This Policy Applies to Your <WidowBlock>Personal Information</WidowBlock>
        </Typography>
        <Typography nextElement='typography'>
          This Policy applies solely to residents of the State of California who are financial representatives; financial representative applicants;
          and District Agents, General Agents, and their staff &#40;&#34;California sales force,&#34; &#34;you,&#34; or
          &#34;your&#34;&#41;. References to the &#34;Northwestern Mutual Family of Companies,&#34; &#34;NM,&#34; &#34;we,&#34;
          &#34;us,&#34; or &#34;our&#34; within this Policy mean The Northwestern Mutual Life Insurance Company and its subsidiaries
          &#40;e.g., Northwestern Long Term Care Company, Northwestern Mutual Investment Management Company, LLC, Northwestern Mutual Wealth Management Company, Northwestern Mutual
          Investment Services, LLC, and Mason Street Advisors, LLC&#41;. The purpose of this Policy is to provide you with a description of your
          rights and our information practices. This Policy also supplements the information contained in the Online Privacy Statement and related
          disclosures of NM.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          When this Privacy Notice Does Not Apply to Your <WidowBlock>Personal Information</WidowBlock>
        </Typography>
        <Typography nextElement='typography'>
          Please note that this Policy does not apply when your Personal Information is protected under other laws, such as the Gramm, Leach Bliley
          Act &#40;GLBA&#41;; the California Financial Information Protection Act; the Health Insurance Portability and Accountability Act
          &#40;HIPAA&#41;; the Fair Credit Reporting Act &#40;FCRA&#41;; and&#47;or the Driver&#39;s License Protection Act. Therefore, this Policy
          does not apply to Personal Information collected in relation to the products and services we provide as a service provider, such as
          insurance products, investment products and services, or financial planning. For a description of your rights and our information practices
          in these instances, please read our&#160;
          <Link
            id='california-agents-policy-notice-privacy-notices-link'
            variant='primaryDeemphasize'
            linkUrl='/privacy-notices/'>
            Privacy Notices
          </Link>.
        </Typography>
        <Typography nextElement='typography'>
          Also, this Policy does not apply to Personal Information collected with respect to workforce&#45;type relationships with NM. This includes
          Personal Information of our applicants, employees, directors, officers, members, and non&#45;agent contractors. To understand more about our
          workforce&#45;related privacy practices, go to the&#160;
          <Link
            id='california-agents-policy-notice-privacy-link'
            variant='primaryDeemphasize'
            linkUrl='/california-workforce-policy-notice/'>
            California Workforce Privacy Policy
          </Link>.
        </Typography>
        <Typography
          component='h3'
          nextElement='typography'>
          Defined Terms
        </Typography>
        <Typography nextElement='graphic'>
          For purposes of this Policy, the terms &#34;Personal Information,&#34; &#34;Sensitive Personal Information,&#34;
          &#34;consumer,&#34; &#34;business purpose,&#34; &#34;commercial purpose,&#34; &#34;third party,&#34; &#34;service
          provider,&#34; &#34;share,&#34; &#34;sell,&#34; and &#34;sold&#34; have the same meanings as provided in the California
          Consumer Privacy Act of 2018 and the California Privacy Rights Act of 2020 &#40;collectively, &#34;CPRA&#34;&#41;.
        </Typography>
        <Accordion
          id='faq-california-agents-policy-notice'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          addFAQPageSchemaItemType={false}
          items={[
            {
              id: 'california-agents-policy-notice-categories-of-personal-information-we-collect',
              title: <><SpanStyledPrintOnly>1. </SpanStyledPrintOnly>Categories of Personal Information We Collect</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography>
                    Over the prior 12 months we may have collected the following categories of Personal Information from our California sales force:
                  </Typography>
                  <TableStyled>
                    <THeadStyled>
                      <tr>
                        <th>Category of Personal Information</th>
                        <th>Examples</th>
                      </tr>
                    </THeadStyled>
                    <tbody>
                      <tr>
                        <TDStyled>A. Personal Information and Identifiers</TDStyled>
                        <TDStyled>
                          Name, alias, Social Security number, date of birth, physical or electronic signature, home address, email address, telephone
                          number, driver&#39;s license number or state identification card, passport number, and other similar identifiers
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>B. Demographics</TDStyled>
                        <TDStyled>
                          Age, race, ethnicity, sex, gender identification, disability status, citizenship, marital status, family member information
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>C. Professional or Employment Information</TDStyled>
                        <TDStyled>
                          Compensation, benefits, beneficiary designations, sales, disciplinary action, work contract&#40;s&#41;, work history, legal
                          compliance reviews, professional designations, veteran or military status, contract files, training, visa status, business
                          expenses, use of company products, and pre&#45;hire documents &#40;such as sales force applications, resumes, background
                          check information, drug test information, and candidate evaluations&#41;
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>D. Education Information</TDStyled>
                        <TDStyled>
                          School records, school&#40;s&#41; attended, dates attended, degree&#40;s&#41; earned, academic achievements
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>E. Financial Information</TDStyled>
                        <TDStyled>
                          Retirement account information, bank accounts, investment or brokerage accounts, information on personal property and real
                          estate, student loans, insurance, information regarding estate or tax planning, debts, trusts, credit or debit card
                          number&#40;s&#41;, legal issues &#40;e.g., child support, alimony, wage garnishments and subpoenas&#41;, and benefits
                          information
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>F. Medical Information</TDStyled>
                        <TDStyled>
                          Medical history; medical questionnaires; information regarding physical, mental, and behavioral health; genetic information;
                          physical characteristics or description; wellness activities and subsidies; health insurance information; medical condition;
                          information regarding payment for healthcare services
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>G. Biometric Information</TDStyled>
                        <TDStyled>
                          Fingerprints, facial scans, eye scans, voice recognition, typing cadence, and any other biometric identifier that we may use
                          for security, or other operational purposes
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>H. Internet or Network Activity</TDStyled>
                        <TDStyled>
                          Browser or device information; browsing information; IP address; company device identifiers; cookies, web beacons, pixel
                          tags, and clickstream or other traffic data; use of IT resources; interaction with our websites, mobile app, advertisements
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>I. Profile Information</TDStyled>
                        <TDStyled>
                          Profiles reflecting a person&#39;s preferences, such as interests, hobbies, characteristic tendencies, behaviors, attitudes,
                          or aptitudes, including inferences drawn from Personal Information
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>J. Product Information</TDStyled>
                        <TDStyled>
                          Policy&#47;account number&#40;s&#41;, policy&#47;account values, beneficiary, ownership arrangements, transaction history
                        </TDStyled>
                      </tr>
                      <tr>
                        <TDStyled>K. Audiovisual Information</TDStyled>
                        <TDStyled>On&#45;site security cameras, pictures, video and audio recordings</TDStyled>
                      </tr>
                    </tbody>
                  </TableStyled>
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-categories-of-sources-of-personal-information',
              title: <><SpanStyledPrintOnly>2. </SpanStyledPrintOnly>Categories of Sources of Personal Information</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    We receive the categories of Personal Information listed above from the following categories of sources:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: <>Directly from you or your authorized agent</> },
                      { bulletText: <>Indirectly from you or your interactions with our technologies, websites, or mobile app</> },
                      {
                        bulletText: (
                          <>
                            Inferences drawn from other Personal Information to create a profile about, for example, your preferences and
                            characteristics
                          </>
                        ),
                      },
                      { bulletText: <>Publicly available information</> },
                      { bulletText: <>Our Northwestern Mutual Family of Companies, including our sales force</> },
                      { bulletText: <>Research or analytics companies</> },
                      { bulletText: <>Marketing or media companies</> },
                      { bulletText: <>Medical providers</> },
                      { bulletText: <>Consumer reporting agencies</> },
                      { bulletText: <>Referrals</> },
                      { bulletText: <>Other service providers</> },
                    ]}
                  />
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-purposes-of-collection-and-disclosure-of-personal-information',
              title: <><SpanStyledPrintOnly>3. </SpanStyledPrintOnly>Purposes of Collection and Disclosure of Personal Information</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    We may use or disclose the Personal Information we collect for purposes that may be described to you at the time of collection
                    and&#47;or one or more of the following purposes:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      {
                        bulletText: (
                          <>
                            <Typography disableBottomPadding>
                              California sales force management:
                            </Typography>
                            <ul>
                              <li>
                                <Typography>
                                  To evaluate you for a sales force position with us when you apply for a position or we receive your information
                                  related to a sales force position at NM
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  To comply with state and federal laws requiring employers to maintain certain records
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  To process payroll and manage applicable tax withholding and reporting
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  To administer and maintain group health insurance benefits, additional wellness programs, profit&#45;sharing
                                  and&#47;or retirement plans, life insurance, disability insurance, leave programs and additional fringe benefit
                                  programs
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  To analyze aspects of performance of your contractual duties
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  For surveys, research, analysis, and strategic development to implement, maintain and promote an engaging work
                                  experience
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  To review and audit workforces interactions with you and NM&#39;s customers and business partners
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  For your security and the security of our facility
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  To support information technology services to our California sales force
                                </Typography>
                              </li>
                              <li>
                                <Typography>
                                  For emergency training and emergency response
                                </Typography>
                              </li>
                            </ul>
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                      {
                        bulletText: (
                          <>
                            <Typography disableBottomPadding>
                              Research &#38; strategic development:
                            </Typography>
                            <ul>
                              <li>
                                <Typography>
                                  Information collected for business, product, strategy, and technological development, excluding marketing and
                                  advertising activities
                                </Typography>
                              </li>
                            </ul>
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                      {
                        bulletText: (
                          <>
                            <Typography disableBottomPadding>
                              Detecting and protecting:
                            </Typography>
                            <ul>
                              <li>
                                <Typography>
                                  Detecting and protecting against security incidents and malicious, deceptive, fraudulent, or illegal activity, or
                                  violations of NM policies or the law
                                </Typography>
                              </li>
                              <li>
                                <Typography>For fraud and crime detection or prevention</Typography>
                              </li>
                              <li>
                                <Typography>For information protection and cybersecurity</Typography>
                              </li>
                            </ul>
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                      {
                        bulletText: (
                          <>
                            <Typography disableBottomPadding>
                              Legal&#47;Compliance&#47;Regulatory purposes:
                            </Typography>
                            <ul>
                              <li>
                                <Typography>
                                  To meet additional legal, compliance, and regulatory requirements and to defend NM&#39;s rights and property
                                </Typography>
                              </li>
                            </ul>
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                      {
                        bulletText: (
                          <>
                            <Typography disableBottomPadding>
                              Online auditing:
                            </Typography>
                            <ul>
                              <li>
                                <Typography>
                                  Auditing related to a current interaction with our California sales force, concurrent transactions, and auditing
                                  compliance with standards
                                </Typography>
                              </li>
                            </ul>
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                      {
                        bulletText: (
                          <>
                            <Typography disableBottomPadding>
                              Debugging:
                            </Typography>
                            <ul>
                              <li>
                                <Typography>
                                  To debug errors in systems
                                </Typography>
                              </li>
                            </ul>
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                      {
                        bulletText: (
                          <>
                            <Typography disableBottomPadding>
                              Backups and Archives:
                            </Typography>
                            <ul>
                              <li>
                                <Typography>
                                  For disaster recovery, business continuity and record keeping obligations
                                </Typography>
                              </li>
                            </ul>
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-use-and-disclosure-of-sensitive-personal-information',
              title: <><SpanStyledPrintOnly>4. </SpanStyledPrintOnly>Use and Disclosure of Sensitive Personal Information</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Sensitive Personal Information is Personal Information that is not publicly available, is collected for the purpose of inferring
                    characteristics about a consumer and reveals:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: <>Social Security, driver&#39;s license, state identification card, or passport number</> },
                      { bulletText: ('Account login or financial account number in combination with any required security or access code, password, or credentials allowing access to an account') },
                      { bulletText: 'Precise geolocation' },
                      { bulletText: 'Racial or ethnic origin, religious or philosophical beliefs, or union membership' },
                      { bulletText: 'Contents of email and text messages, unless NM is the intended recipient' },
                      { bulletText: 'Genetic information' },
                      { bulletText: 'Processing of biometric information for the purpose of uniquely identifying a consumer' },
                      {
                        bulletText: (
                          <>Personal Information collected and analyzed concerning a consumer&#39;s health, sex life, and&#47;or sexual orientation</>
                        ),
                      },
                    ]}
                  />
                  <Typography disableBottomPadding>
                    When we collect Sensitive Personal Information about you, we only use or disclose it for the following purposes:
                  </Typography>
                  <BulletedList
                    bulletType='orderedLowerAlpha'
                    bulletedListData={[
                      { bulletText: 'To perform services or provide goods reasonably expected by an average consumer who requests those goods or services.' },
                      { bulletText: 'To prevent, detect, and investigate security incidents that compromise the availability, authenticity, integrity, or confidentiality of stored or transmitted Personal Information.' },
                      { bulletText: 'To resist malicious, deceptive, fraudulent, or illegal actions directed at us and to prosecute those responsible for those actions.' },
                      { bulletText: 'To ensure the physical safety of natural persons.' },
                      {
                        bulletText: <>For short&#45;term, transient use, including, but not limited to, non&#45;personalized advertising shown as part of a
                          consumer&#39;s current interaction with us when Personal Information is not disclosed to another third party and is not used
                          to build a profile about the consumer or otherwise alter the consumer&#39;s experience outside the current interaction with
                          us.</>,
                      },
                      { bulletText: 'To perform services on our behalf, such as maintaining or servicing accounts, providing customer service, processing, or fulfilling orders and transactions, verifying customer information, processing payments, providing financing, providing analytic services, providing storage, or providing similar services.' },
                      { bulletText: 'To verify or maintain the quality or safety of a product, service, or device that is owned, manufactured, manufactured for, or controlled by us, and to improve, upgrade, or enhance the service or device that is owned, manufactured by, manufactured for, or controlled by us.' },
                      { bulletText: 'For purposes that do not infer characteristics about a consumer.' },
                      { bulletText: 'Because we do not use or disclose Sensitive Personal Information for the purpose of inferring characteristics about you and because we limit our use and disclosure of Sensitive Personal Information to those purposes specified above, you do not have the right to limit its use and disclosure.' },
                    ]}
                  />
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-personal-information-sold-or-shared-to-third-parties',
              title: <><SpanStyledPrintOnly>5. </SpanStyledPrintOnly>Personal Information Sold or Shared to Third Parties</>,
              contentAs: 'div',
              content: (
                <Typography>
                  In the preceding 12 months, we have not sold or shared any Personal Information to third parties. This includes Personal Information of California sales force under the age of 16.
                </Typography>
              ),
            },
            {
              id: 'california-agents-policy-notice-categories-of-personal-information-disclosed',
              title: <><SpanStyledPrintOnly>6. </SpanStyledPrintOnly>Categories of Personal Information Disclosed</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography>
                    In the preceding 12 months, we may have disclosed your Personal Information for a business or commercial purpose, as permitted or
                    required by law or as otherwise set forth in this Policy. When we disclose Personal Information to a service provider, we enter
                    into a contract that describes our business purpose for disclosing and requires the service provider to keep Personal Information
                    confidential and to use it only for purposes of performing the contract between us.
                  </Typography>
                  <Typography disableBottomPadding>
                    We may disclose all categories of your Personal Information for a business or commercial purpose to the following:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: 'Parties to whom you have directed or authorized our disclosure' },
                      { bulletText: 'Our Northwestern Mutual Family of Companies, including our sales force' },
                      { bulletText: 'Research or analytics companies' },
                      { bulletText: 'Marketing or media companies' },
                      {
                        bulletText: (
                          'Regulators and others with legal authority, such as law enforcement agencies, government authorities, parties with a valid subpoena, and others as permitted or required by law'
                        ),
                      },
                      {
                        bulletText: (
                          <>
                            Other contracted service providers, including individuals, firms, consultants, vendors and technologies&#45;providing
                            services, software, platforms, or tools that are used to perform business functions for our Northwestern Mutual Family of
                            Companies and sales force.
                          </>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-retention-of-personal-information',
              title: <><SpanStyledPrintOnly>7. </SpanStyledPrintOnly>Retention of Personal Information</>,
              contentAs: 'div',
              content: (
                <Typography>
                  We have policies and practices requiring the secure deletion of Personal Information when there is no applicable regulatory retention requirement and we no longer have a business need to use the information for a purpose that is compatible with our disclosed purposes of collection. Personal Information that has been aggregated or deidentified so that it cannot reasonably be used to infer information about you or otherwise be linked to you may be retained indefinitely.
                </Typography>
              ),
            },
            {
              id: 'california-agents-policy-notice-your-rights',
              title: <><SpanStyledPrintOnly>8. </SpanStyledPrintOnly>Your Rights</>,
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  As detailed in our polices on information security, you have no right to privacy in your use of NM information technology resources, including emails and communications for work purposes that constitute NM business records and are monitored and may be reviewed or disclosed at any time without further prior notice for compliance, legal and other operational needs. However, you do have rights under CPRA to submit requests with respect to your Personal Information.
                </Typography>
              ),
            },
            {
              id: 'california-agents-policy-notice-right-to-access',
              title: <><SpanStyledPrintOnly>8.a. </SpanStyledPrintOnly>Right to Access</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography>
                    You have the right to access your specific Personal Information and to know about our collection, use, disclosure, and sharing of
                    your Personal Information.
                  </Typography>
                  <Typography disableBottomPadding>
                    When you or your authorized agent submit a request to know or access, we will verify the identity and authority of the person
                    making the request, confirm we have Personal Information about you, and validate that the CPRA applies to your information. Once
                    we have taken these steps, we will disclose:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: <>The categories of Personal Information we have collected about you;</> },
                      { bulletText: <>The categories of sources from which Personal Information was collected;</> },
                      { bulletText: <>Our business or commercial purpose for collecting, selling, or sharing Personal Information;</> },
                      { bulletText: <>The categories of third parties to whom we disclose Personal Information;</> },
                      {
                        bulletText: (
                          <>
                            The specific pieces of Personal Information obtained from you that we are authorized and required to produce under CPRA;
                            and
                          </>
                        ),
                      },
                      {
                        bulletText: (
                          <>
                            <Typography
                              nextElement='typography'
                              disableBottomPadding>
                              If we have disclosed, shared or sold your Personal Information, two separate lists identifying:
                            </Typography>
                            <BulletedList
                              bulletType='orderedLowerRoman'
                              bulletedListData={[
                                { bulletText: 'the categories of Personal Information disclosed for a business purpose and the categories of persons to whom Personal Information was disclosed; and' },
                                { bulletText: 'the categories of Personal Information shared or sold and the categories of third parties with whom Personal Information was shared or sold.' },
                              ]}
                            />
                          </>
                        ),
                        useRawImportedBulletContent: true,
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-right-to-delete',
              title: <><SpanStyledPrintOnly>8.b. </SpanStyledPrintOnly>Right to Delete</>,
              contentAs: 'div',
              content: (
                <Typography>
                  You have the right to request that we delete Personal Information we collected from you, subject to certain exceptions allowed under applicable law. Once we receive and confirm your verifiable consumer request, we will delete your Personal Information from our records unless an exception applies. We will also notify, if possible, our service providers, contractors and third parties of your deletion request. If we do not delete your Personal Information, we will provide you with an explanation of why and limit our use of your Personal Information to the reasons we are retaining it.
                </Typography>
              ),
            },
            {
              id: 'california-agents-policy-notice-right-to-correct',
              title: <><SpanStyledPrintOnly>8.c. </SpanStyledPrintOnly>Right to Correct</>,
              contentAs: 'div',
              content: (
                <Typography>
                  You have the right to request that we correct any Personal Information we maintain about you to ensure that it is complete, accurate, and as current as possible. We may elect to delete your Personal Information rather than correct it, and we may not be able to accommodate your request if we believe it would violate any law or legal requirement or cause information to be incorrect. If you have an online account with us, you can review and correct Personal Information by logging into the website or mobile app and visiting your &#34;Account&#34; page.
                </Typography>
              ),
            },
            {
              id: 'california-agents-policy-notice-right-to-limit-use',
              title: <><SpanStyledPrintOnly>8.d. </SpanStyledPrintOnly>Right to Limit Use and Disclosure of Sensitive Personal Information</>,
              contentAs: 'div',
              content: (
                <Typography>
                  We limit our use and disclosure of Sensitive Personal Information to those purposes set forth in this Policy and we do not use or disclose Sensitive Personal Information for the purpose of inferring characteristics about any consumer. Should this change in the future, we will update this Policy and provide you with methods to limit our use and disclosure of Sensitive Personal Information.
                </Typography>
              ),
            },
            {
              id: 'california-agents-policy-notice-right-to-no-retaliation',
              title: <><SpanStyledPrintOnly>8.e. </SpanStyledPrintOnly>Right of No Retaliation</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>We will not discriminate against you for exercising your rights, including by:</Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: <>Denying you goods or services.</> },
                      {
                        bulletText: (
                          <>
                            Charging you different prices or rates for goods or services, including through granting discounts or other benefits, or
                            imposing penalties.
                          </>
                        ),
                      },
                      { bulletText: <>Providing you a different level or quality of goods or services.</> },
                      {
                        bulletText: (
                          <>
                            Suggesting that you may receive a different price or rate for goods or services or a different level or quality of goods
                            or services.
                          </>
                        ),
                      },
                    ]}
                  />
                  <Typography>
                    We may, however, charge you a different price or rate or provide a different level or quality of goods or services to you if that
                    difference is reasonably related to the value your data provides to us.
                  </Typography>
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-how-to-submit-your-request',
              title: <><SpanStyledPrintOnly>9. </SpanStyledPrintOnly>How to Submit Your Request</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    To exercise the Right to Access, Delete, or Correct, please submit a verifiable consumer request to us by either:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      {
                        bulletText: (
                          <>
                            Completing the&#160;
                            <Link
                              id='california-agents-policy-notice-sales-force-right-to-access-correct-or-delete-form'
                              variant='primaryDeemphasize'
                              linkUrl='https://privacyportal-na01.onetrust.com/webform/ba631c60-594d-44e1-9224-aeb0fabbe5ac/f43088cc-3bb0-4766-9156-8be1d782e79a/'
                              isOutgoingLink
                            >
                              California Sales Force Right to Access, Correct, or Delete Form
                            </Link>
                          </>
                        ),
                      },
                      {
                        bulletText: (
                          <>
                            Calling us toll&#45;free at{' '}
                            <Link
                              id='client-services-phone-number-link'
                              variant='primaryDeemphasize'
                              linkUrl={clientServicesPhoneNumberLinkUrl}
                              noWrap>
                              <ClientServicesPhone4644 />
                            </Link>
                            , when prompted say &#34;California Consumer Privacy Act,&#34; &#34;CCPA,&#34; &#34;California Privacy Rights Act,&#34; or &#34;CPRA&#34;
                          </>
                        ),
                      },
                    ]}
                  />
                  <Typography>
                    We are not obligated to provide information to you in response to your access request more than twice in a 12&#45;month period.
                  </Typography>
                  <Typography>
                    Only you or your authorized agent &#40;i.e., a person we can validate as being authorized by you&#41; may make a verifiable
                    consumer request related to your Personal Information. If your authorized agent makes a verifiable consumer request and provides
                    proof that you gave them authority to submit the request on your behalf, we will provide the information to you unless your
                    authorized agent requests and, we approve, disclosure directly to them.
                  </Typography>
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-verification-of-identity-and-personal-information',
              title: <><SpanStyledPrintOnly>10. </SpanStyledPrintOnly>Verification of Identity and Personal Information</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography>
                    Whether you submit a request directly on your own behalf, or through an authorized agent, we will take reasonable steps to verify
                    your identity prior to responding to your request. Upon receiving a request to access, delete, or correct your Personal Information,
                    we will confirm receipt within 10 business days. For all requests, we will need your first and last name plus the following information: (i) date of birth and (ii) residential address.
                  </Typography>
                  <Typography disableBottomPadding>
                    When your request is submitted through an authorized agent, we will also take reasonable steps to verify the agent&#39;s identity
                    and authorization to make the request on your behalf. To do this, we will need your agent to provide their first and last name,
                    address, telephone number, date of birth, plus documentation verifying they are
                    authorized to act on your behalf. Examples include:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: <>Court Order of Guardianship or Conservatorship</> },
                      { bulletText: <>Notice of Retainer</> },
                      { bulletText: <>Authorization from you, signed and independently witnessed</> },
                      { bulletText: <>Letters of Guardianship or Conservatorship</> },
                      { bulletText: <>Power of Attorney provided pursuant to California Probate Code sections 4121 to 4130</> },
                    ]}
                  />
                  <Typography>
                    To protect the privacy and security of your Personal Information, we may request additional information from you to help us verify
                    your identity and process your request. Of course, we cannot respond to your request or provide you with Personal Information if
                    we cannot &#40;i&#41; verify your identity, or the identify of your authorized agent, and &#40;ii&#41; confirm that Personal
                    Information we have directly relates to you. You will also be asked to make a declaration under penalty of perjury that you are
                    the consumer who is the subject of the request.
                  </Typography>
                  <Typography>
                    Making a verifiable consumer request does not require you to create an account with us. We will only use Personal Information
                    provided in a request to access, correct, or delete to verify the requestor&#39;s identity or authority to make the request and to
                    confirm Personal Information we have directly relates to the person who is the subject of the request.
                  </Typography>
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-response-timing-and-format',
              title: <><SpanStyledPrintOnly>11. </SpanStyledPrintOnly>Response Timing and Format</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography>
                    We endeavor to respond to a verifiable consumer request within 45 days of its receipt. If we require more time &#40;up to 90
                    days&#41;, we will inform you in writing of the reason and extension period. We will deliver our response by mail or
                    electronically, at your option. Any response we provide will cover the 12&#45;month period preceding our receipt of the verifiable
                    consumer request unless you specifically request information beyond such period. The response will also explain the reasons we
                    cannot comply with a request, if applicable.
                  </Typography>
                  <Typography>
                    Requests to exercise your privacy rights are generally free. If we determine that the request warrants a fee, we will tell you why
                    we made that decision and provide you with a reasonable cost estimate before completing your request.
                  </Typography>
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-changes-to-our-privacy-notice',
              title: <><SpanStyledPrintOnly>12. </SpanStyledPrintOnly>Changes to this Policy</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography>
                    We may change this Policy from time to time. When we make changes to this Policy, we will post the updated Policy on the Privacy
                    pages of our website with a new &#34;Last Updated&#34; date. Any changes will become effective when we post the updated
                    Policy.
                  </Typography>
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-notice-for-our-california-sales-force-with-disabilities',
              title: <><SpanStyledPrintOnly>13. </SpanStyledPrintOnly>Notice for Our California Sales Force with Disabilities</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography>
                    We strive to provide you with an accessible digital experience and are committed to providing our California sales force with the
                    same level of access to this Policy, including those with disabilities. Therefore, this Policy is compatible with standard screen
                    readers.
                  </Typography>
                </>
              ),
            },
            {
              id: 'california-agents-policy-notice-contact-information',
              title: <><SpanStyledPrintOnly>14. </SpanStyledPrintOnly>Contact Information</>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    If you have any questions or comments about this Privacy Policy or need accommodation to access this Privacy Policy, you may
                    contact us at:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      {
                        bulletText: (
                          <>
                            Toll Free Phone:{' '}
                            <Link
                              id='california-agents-policy-notice-contact-information-client-services-phone-number-link'
                              variant='primaryDeemphasize'
                              linkUrl={clientServicesPhoneNumberLinkUrl}
                              noWrap>
                              <ClientServicesPhone4644 />
                            </Link>
                            , when prompted say &#34;California Consumer Privacy Act,&#34; &#34;CCPA,&#34; &#34;California Privacy Rights Act,&#34; or &#34;CPRA&#34;
                          </>
                        ),
                      },
                      {
                        bulletText: <>
                          Email:{' '}
                          <Link
                            id='california-agents-policy-notice-contact-information-service-link'
                            variant='primaryDeemphasize'
                            linkUrl='mailto:customerservice8200@northwesternmutual.com'>
                            customerservice8200&#64;northwesternmutual.com
                          </Link>
                        </>,
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
